/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { ContentBlock, Heading, Text, Button, Icon, CardCarousel } from 'sarsaparilla';

import { useUserLocation } from 'shared-ui/geolocation';

import {
    useCampsitesAvailableThisWeekendQuery,
    RECOMMENDATION_RADIUS,
} from './api/search-api.queries';
import { getNextWeekend } from './utils/getNextWeekend';
import { CarouselCard } from './CarouselCard/CarouselCard';
import { RecommendationCarouselSkeleton } from './RecommendationCarouselSkeleton';
import { dateToSillySearchFormat } from './utils';

/**
 * Renders a carousel of campsites available nearby the user's location.
 */
export function AvailableNearby() {
    const userLocation = useUserLocation();

    const isInUS = userLocation?.isInUS ?? false;

    const { data, isError, isPending } = useCampsitesAvailableThisWeekendQuery({
        isEnabled: userLocation !== null,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        location: userLocation?.location,
    });

    const cards = React.useMemo(() => {
        if (!data?.results) {
            return [];
        }

        return data?.results?.map((item) => {
            return (
                <CarouselCard
                    key={item.id}
                    item={item}
                    showAvailDates
                    onClick={() => {}}
                    gaTrackingId="104954534727"
                    gaTrackingValue={item.name}
                />
            );
        });
    }, [data?.results]);

    const exploreMoreHref = React.useMemo(() => {
        if (!userLocation) {
            return '';
        }

        return getExploreMoreHref(userLocation.latitude, userLocation.longitude);
    }, [userLocation]);

    if (isPending) {
        return <RecommendationCarouselSkeleton />;
    }

    // Render nothing is there's nothing to show
    if (isError || !data?.results || data.results.length <= 4) {
        return null;
    }

    return (
        <section>
            <ContentBlock>
                <Heading headingLevel={2} appearance="h4" hasUnderline="left">
                    {isInUS ? 'Available Nearby' : 'Available This Weekend'}
                </Heading>
                <Text className="mb-2">
                    Campgrounds with upcoming availability.{' '}
                    <Button
                        appearance="link"
                        href={exploreMoreHref}
                        iconAfterElement={<Icon iconName="arrow-forward" />}
                        className="ml-half"
                        style={{ verticalAlign: 'baseline' }}
                        aria-label="Explore more nearby available campsites"
                        gaTrackingId="706108334029"
                    >
                        Explore More
                    </Button>
                </Text>
            </ContentBlock>

            <CardCarousel
                accessibleName="Available Nearby"
                gaTrackingId="565733116163"
                overscanPerBreakpoint={{ md: 8 }}
                simulateContentBlock
                peekPerBreakpoint={{ xs: 24, md: 0 }}
            >
                {cards}
            </CardCarousel>
        </section>
    );
}

function getExploreMoreHref(latitude: number, longitude: number): string {
    const [startDate, endDate] = getNextWeekend();

    const searchParams = new URLSearchParams({
        inventory_type: 'camping',
        checkin: dateToSillySearchFormat(startDate),
        checkout: dateToSillySearchFormat(endDate),
        sort: 'available',
        lat: latitude.toString(),
        lng: longitude.toString(),
        clear: 'true',
        radius: RECOMMENDATION_RADIUS,
    });

    return `/search?${searchParams.toString()}`;
}

// cSpell:ignore checkin
